import { FormattedDateTime, Badge, Link } from '@customerio/pluma-components/react';
import { isRecord } from '@/utils/objects';
import type { IdentifierConfig } from '../display-name';
import {
  containsIdentifiersForLogType,
  isIdentifierAttr,
  isSegmentMembershipArrayKey,
  isUrl,
  isValidTimestamp,
  reservedAttributesForLogType,
  toSegmentId,
} from '../json-format-utils';
import { formatLogReason } from '../reason-format';
import * as styles from './log-json-formatter.css';

/**
 * Resolves a segment id (from an `entered`/`exited` array) to a name + href, or `null` when the
 * segment is gone. Supplied by the page from the sideloaded segment map.
 */
export type SegmentLinkResolver = (segmentId: number) => { name: string; href: string } | null;

/** A resolved reference for a root id key — its display name, optionally linked. */
export type GeoRef = { name: string; href?: string };

/**
 * Resolves a root key/value pair (e.g. `geoset_id` / `geofence_id` on a geofence transition
 * event) to a name annotation, or `null` when the key isn't a resolvable reference (or the
 * referenced entity is gone — the raw id then renders unannotated).
 */
export type GeoRefResolver = (key: string, value: unknown) => GeoRef | null;

/**
 * Renders an activity log's `attrs` as a syntax-highlighted JSON tree. Unlike Pluma's `CodeBlock`
 * (Shiki, no custom content), this is a React tree, so it injects:
 *  - "Identifier" / "Reserved" labels on root keys (per the log type),
 *  - humanized timestamp annotations after unix-timestamp values,
 *  - links for URL string values and segment ids, and channel-formatted error reasons.
 *
 * Styled to resemble the Pluma `CodeBlock` (dark charcoal mono panel); see the .css.ts.
 *
 * The classification (identifier/reserved) is activity-log-shaped for now. If a second consumer
 * needs a JSON tree, promote this to a shared component with the classification passed in as props.
 */

type FormatterContext = {
  identifiers: IdentifierConfig;
  containsIdentifiers: boolean;
  reservedAttributes: readonly string[];
  showNestedIdentifiers: boolean;
  resolveSegmentLink?: SegmentLinkResolver;
  resolveGeoRef?: GeoRefResolver;
  /** Delivery type used to format a top-level `reason` value. */
  formatErrorType?: string | null;
  /** Raw log type — used to format the `reason` for `failed_object_journeys`. */
  logType: string;
};

type KeyBadgeProps = { name: string; isRootKey: boolean; ctx: FormatterContext };

/** The "Identifier" / "Reserved" Tag for a key, or null. */
function KeyBadge({ name, isRootKey, ctx }: KeyBadgeProps) {
  if (!isRootKey && !ctx.showNestedIdentifiers) return null;
  const isIdentifier = ctx.containsIdentifiers && isIdentifierAttr(name, ctx.identifiers);
  const isReserved = ctx.reservedAttributes.includes(name);
  if (!isIdentifier && !isReserved) return null;
  // Label (not Tag): Tag blended into the panel in dark mode. Label's colored chip stays distinct
  // in both themes — blue for identifiers (informational), grey/neutral for reserved attributes.
  return (
    <span className={styles.badge}>
      <Badge color={isIdentifier ? 'blue' : 'grey'}>{isIdentifier ? 'Identifier' : 'Reserved'}</Badge>
    </span>
  );
}

type TimestampAnnotationProps = { value: string | number; keyName?: string; ctx: FormatterContext };

/** Muted humanized timestamp after a raw value, when it looks like a unix timestamp. */
function TimestampAnnotation({ value, keyName, ctx }: TimestampAnnotationProps) {
  // Skip the annotation when the key is an identifier (e.g. a numeric id).
  if (keyName != null && isIdentifierAttr(keyName, ctx.identifiers)) return null;
  if (!isValidTimestamp(value)) return null;
  return (
    <span className={styles.timestamp} data-test-id="json-formatter-timestamp">
      <FormattedDateTime date={Number(value)} format="medium_with_time" />
    </span>
  );
}

type StringValueProps = { value: string; keyName?: string; ctx: FormatterContext };

function StringValue({ value, keyName, ctx }: StringValueProps) {
  if (isUrl(value)) {
    // Pluma Link owns link colors/hover; isExternal → opens in a new tab.
    // Quotes stay outside the link, matching the surrounding string rendering.
    return (
      <>
        "
        <Link href={value} isExternal isUnderlined>
          {value}
        </Link>
        "
      </>
    );
  }
  return (
    <>
      <span className={styles.valueString}>"{value}"</span>
      <TimestampAnnotation value={value} keyName={keyName} ctx={ctx} />
    </>
  );
}

type JsonValueProps = { value: unknown; keyName?: string; ctx: FormatterContext };

function JsonValue({ value, keyName, ctx }: JsonValueProps) {
  if (value === null || value === undefined) return <span className={styles.valueNull}>null</span>;
  if (Array.isArray(value)) return <ArrayValue items={value} parentKey={keyName} ctx={ctx} />;
  if (isRecord(value)) {
    return <ObjectValue object={value} isRoot={false} ctx={ctx} />;
  }
  if (typeof value === 'number') {
    return (
      <>
        <span className={styles.valueNumber}>{value}</span>
        <TimestampAnnotation value={value} keyName={keyName} ctx={ctx} />
      </>
    );
  }
  if (typeof value === 'boolean') return <span className={styles.valueBool}>{String(value)}</span>;
  return <StringValue value={String(value)} keyName={keyName} ctx={ctx} />;
}

type GeoRefAnnotationProps = { geoRef: GeoRef };

/** Muted resolved-name annotation after a raw geoset/geofence id, linked when it has a page. */
function GeoRefAnnotation({ geoRef }: GeoRefAnnotationProps) {
  return (
    <span className={styles.timestamp} data-test-id="json-formatter-geo-ref">
      {geoRef.href != null ? (
        <Link href={geoRef.href} isUnderlined>
          {geoRef.name}
        </Link>
      ) : (
        geoRef.name
      )}
    </span>
  );
}

type SegmentArrayItemProps = { id: number; ctx: FormatterContext };

/** A segment id inside an `entered`/`exited` array → name link, or "Deleted segment (id)". */
function SegmentArrayItem({ id, ctx }: SegmentArrayItemProps) {
  const segment = ctx.resolveSegmentLink?.(id) ?? null;
  if (segment == null) {
    return <span className={styles.valueNull}>Deleted segment ({id})</span>;
  }
  return (
    <Link href={segment.href} isUnderlined>
      {segment.name}
    </Link>
  );
}

type ArrayValueProps = { items: unknown[]; parentKey?: string; ctx: FormatterContext };

function ArrayValue({ items, parentKey, ctx }: ArrayValueProps) {
  if (items.length === 0) return <span className={styles.punctuation}>[ ]</span>;
  const isSegmentArray = isSegmentMembershipArrayKey(parentKey) && ctx.resolveSegmentLink != null;
  return (
    <>
      <span className={styles.punctuation}>[</span>
      {/* oxlint-disable-next-line pluma/enforce-pluma-elements -- semantic nesting for the JSON syntax tree; list visuals are fully reset, Pluma List layout doesn't apply. */}
      <ul className={styles.nested}>
        {items.map((item, i) => {
          // Segment ids may serialize as number or numeric string — link both (our resolver
          // coerces with Number(), so a string id resolves the same).
          const segmentId = isSegmentArray ? toSegmentId(item) : null;
          return (
            // oxlint-disable-next-line pluma/enforce-pluma-elements -- see the <ul> note above.
            <li key={i} className={styles.row}>
              {segmentId != null ? <SegmentArrayItem id={segmentId} ctx={ctx} /> : <JsonValue value={item} ctx={ctx} />}
              {i < items.length - 1 ? <span className={styles.punctuation}>,</span> : null}
            </li>
          );
        })}
      </ul>
      <span className={styles.punctuation}>]</span>
    </>
  );
}

type EntryValueProps = { name: string; value: unknown; isRoot: boolean; ctx: FormatterContext };

/**
 * An entry's rendered value: a top-level `reason` is formatted per the delivery type (twilio doc
 * link, …) with a null result falling back to the raw value, rendered as a quoted string.
 */
function EntryValue({ name, value, isRoot, ctx }: EntryValueProps) {
  const formattedReason =
    isRoot && name === 'reason'
      ? formatLogReason(value, { deliveryType: ctx.formatErrorType, logType: ctx.logType })
      : null;
  if (formattedReason != null) {
    return <span className={styles.valueString}>"{formattedReason}"</span>;
  }
  return <JsonValue value={value} keyName={name} ctx={ctx} />;
}

/** Rename the legacy `prefetched` metric key to `machine` in the UI. */
function entryDisplayKey(name: string): string {
  return name === 'prefetched' ? 'machine' : name;
}

type ObjectEntryProps = {
  name: string;
  value: unknown;
  isRoot: boolean;
  isLast: boolean;
  ctx: FormatterContext;
};

function ObjectEntry({ name, value, isRoot, isLast, ctx }: ObjectEntryProps) {
  const geoRef = isRoot ? (ctx.resolveGeoRef?.(name, value) ?? null) : null;
  return (
    // oxlint-disable-next-line pluma/enforce-pluma-elements -- semantic nesting for the JSON syntax tree; list visuals are fully reset, Pluma List layout doesn't apply.
    <li className={styles.row}>
      <span className={styles.propKey}>{entryDisplayKey(name)}</span>
      <KeyBadge name={name} isRootKey={isRoot} ctx={ctx} />
      <span className={styles.punctuation}>: </span>
      <EntryValue name={name} value={value} isRoot={isRoot} ctx={ctx} />
      {geoRef != null ? <GeoRefAnnotation geoRef={geoRef} /> : null}
      {isLast ? null : <span className={styles.punctuation}>,</span>}
    </li>
  );
}

type ObjectValueProps = { object: Record<string, unknown>; isRoot: boolean; ctx: FormatterContext };

function ObjectValue({ object, isRoot, ctx }: ObjectValueProps) {
  const entries = Object.entries(object);
  if (entries.length === 0) return <span className={styles.punctuation}>{'{ }'}</span>;
  return (
    <>
      <span className={styles.punctuation}>{'{'}</span>
      {/* oxlint-disable-next-line pluma/enforce-pluma-elements -- semantic nesting for the JSON syntax tree; list visuals are fully reset, Pluma List layout doesn't apply. */}
      <ul className={styles.nested}>
        {entries.map(([key, value], i) => (
          <ObjectEntry key={key} name={key} value={value} isRoot={isRoot} isLast={i === entries.length - 1} ctx={ctx} />
        ))}
      </ul>
      <span className={styles.punctuation}>{'}'}</span>
    </>
  );
}

type LogJsonFormatterProps = {
  data: unknown;
  logType: string;
  identifiers: IdentifierConfig;
  /** Whether to show identifier/reserved badges on nested keys too (profile_merge). */
  showNestedIdentifiers?: boolean;
  /** Resolves segment ids in `entered`/`exited` arrays to name links (segment-membership logs). */
  resolveSegmentLink?: SegmentLinkResolver;
  /** Resolves root id keys (`geoset_id` / `geofence_id`) to name annotations (geofence logs). */
  resolveGeoRef?: GeoRefResolver;
  /** Delivery type used to format a top-level `reason` value. */
  formatErrorType?: string | null;
};

export function LogJsonFormatter({
  data,
  logType,
  identifiers,
  showNestedIdentifiers = false,
  resolveSegmentLink,
  resolveGeoRef,
  formatErrorType,
}: LogJsonFormatterProps) {
  const ctx: FormatterContext = {
    identifiers,
    containsIdentifiers: containsIdentifiersForLogType(logType),
    reservedAttributes: reservedAttributesForLogType(logType),
    showNestedIdentifiers,
    resolveSegmentLink,
    resolveGeoRef,
    formatErrorType,
    logType,
  };
  return (
    <div className={styles.root} data-test-id="log-json-formatter">
      {isRecord(data) ? <ObjectValue object={data} isRoot ctx={ctx} /> : <JsonValue value={data} ctx={ctx} />}
    </div>
  );
}
